import { Button, InputNumber, InputNumberProps, Segmented, Space, Typography } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { TbArrowDown, TbArrowUp, TbMinus, TbPlus } from "react-icons/tb";

export const StepHeading = ({ step, title, description }: { step: number; title: string; description?: string }) => (
  <div className="mb-6">
    <div className="text-xs font-semibold uppercase tracking-wider text-violet-700 mb-1">Step {step} of 4</div>
    <Typography.Title level={4} className="!m-0 !mb-1">
      {title}
    </Typography.Title>
    {description && <Typography.Text className="text-gray-600">{description}</Typography.Text>}
  </div>
);

export const Question = ({
  title,
  hint,
  children,
  className = "",
}: {
  title: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div className={`mb-6 ${className}`}>
    <div className="text-[15px] font-semibold text-gray-900">{title}</div>
    {hint && <div className="text-[13px] text-gray-600 mt-0.5">{hint}</div>}
    <div className="mt-2.5">{children}</div>
  </div>
);

export type ChoiceOption<T extends string> = {
  value: T;
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Small illustration shown above the title. */
  visual?: ReactNode;
};

/** Large selectable cards, used as a controlled form input (value / onChange). */
export const ChoiceCards = <T extends string>({
  value,
  onChange,
  options,
  columns = 1,
}: {
  value?: T;
  onChange?: (value: T) => void;
  options: ChoiceOption<T>[];
  columns?: 1 | 2;
}) => (
  <div role="radiogroup" className={`grid gap-2.5 ${columns === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange?.(option.value)}
          className={`flex items-start gap-3 text-left font-[inherit] rounded-xl bg-white px-4 py-3.5 cursor-pointer transition-all border-[1.5px] border-solid ${
            active
              ? "border-violet-700 shadow-[0_0_0_3px_rgba(124,58,237,0.12)]"
              : "border-gray-200 hover:border-violet-300"
          }`}
        >
          {option.icon && (
            <span
              className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 text-xl ${
                active ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-600"
              }`}
            >
              {option.icon}
            </span>
          )}
          <span className="flex flex-col gap-1 grow">
            {option.visual && <span className="block mb-1.5 w-28">{option.visual}</span>}
            <span className="text-sm font-semibold text-gray-900">{option.title}</span>
            {option.description && <span className="text-xs text-gray-600 leading-snug">{option.description}</span>}
          </span>
          <span
            className={`w-[18px] h-[18px] rounded-full shrink-0 flex items-center justify-center border-[1.5px] border-solid ${
              active ? "border-violet-700" : "border-gray-300"
            }`}
          >
            {active && <span className="w-2 h-2 rounded-full bg-violet-700" />}
          </span>
        </button>
      );
    })}
  </div>
);

/** Two answer buttons for a yes / no style question. */
export const AnswerButtons = <T extends string | boolean>({
  value,
  onChange,
  options,
}: {
  value?: T;
  onChange?: (value: T) => void;
  options: { value: T; label: string }[];
}) => (
  <Space>
    {options.map((option) => (
      <Button
        key={String(option.value)}
        color={option.value === value ? "purple" : "default"}
        variant="outlined"
        className={option.value === value ? "!bg-violet-50 font-medium" : ""}
        onClick={() => onChange?.(option.value)}
      >
        {option.label}
      </Button>
    ))}
  </Space>
);

/** Fraction (0-1) stored, percentage shown. */
export const PercentInput = (props: InputNumberProps<number>) => (
  <InputNumber<number>
    min={0}
    max={1}
    step={0.01}
    className="w-full"
    formatter={(value) => (value ? `${Number((Number(value) * 100).toFixed(2))}` : "0")}
    parser={(value) => (value ? Number.parseFloat(value) / 100 : 0)}
    suffix="%"
    {...props}
  />
);

/**
 * Signed fraction for `dynamic_weekly_pushback`: direction switch + absolute percentage.
 * Keeps the direction locally so "Decrease 0%" does not flip back to increase.
 */
export const PushbackInput = ({ value = 0, onChange }: { value?: number; onChange?: (value: number) => void }) => {
  const [direction, setDirection] = useState<"increase" | "decrease">(value < 0 ? "decrease" : "increase");
  useEffect(() => {
    if (value !== 0) setDirection(value < 0 ? "decrease" : "increase");
  }, [value]);

  const emit = (dir: "increase" | "decrease", amount: number) => onChange?.(dir === "decrease" ? -amount : amount);

  return (
    <Space className="w-full max-w-sm">
      <Segmented<"increase" | "decrease">
        value={direction}
        onChange={(dir) => {
          setDirection(dir);
          emit(dir, Math.abs(value));
        }}
        options={[
          { value: "increase", label: "Increase", icon: <TbArrowUp /> },
          { value: "decrease", label: "Decrease", icon: <TbArrowDown /> },
        ]}
        className="!rounded-r-none"
      />
      <PercentInput value={Math.abs(value)} onChange={(val) => emit(direction, val ?? 0)} />
    </Space>
  );
};

/** −/+ counter for small integers. */
export const CountStepper = ({
  value = 0,
  onChange,
  min = 0,
}: {
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
}) => (
  <Space>
    <Button aria-label="Decrease" icon={<TbMinus />} disabled={value <= min} onClick={() => onChange?.(value - 1)} />
    <span className="inline-block min-w-10 text-center text-base font-semibold">{value}</span>
    <Button aria-label="Increase" icon={<TbPlus />} onClick={() => onChange?.(value + 1)} />
  </Space>
);
